import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  Card,
  Button,
  Space,
  Checkbox,
  message,
  Progress,
  Tag,
  Tooltip,
  Modal,
  Input,
  Alert,
} from 'antd'
import type { TableProps } from 'antd'
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
  SendOutlined,
  EyeOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useProjects } from '../hooks'
import { scrapeApi } from '../api/client'
import type { Project, ScrapeStatus } from '../types'
import ProjectInfoDrawer from '../components/ProjectInfoDrawer'

// 抓取状态配置
const scrapeStatusConfig: Record<ScrapeStatus, { color: string; text: string; icon: JSX.Element }> = {
  NOT_SCRAPED: {
    color: 'default',
    text: '未抓取',
    icon: <MinusCircleOutlined />,
  },
  SCRAPED_NO_DATA: {
    color: 'warning',
    text: '无数据',
    icon: <InfoCircleOutlined />,
  },
  SCRAPED_SUCCESS: {
    color: 'success',
    text: '已抓取',
    icon: <CheckCircleOutlined />,
  },
}

interface ScrapeType {
  key: string
  label: string
  field: keyof Project
  statusField: 'email_scrape_status' | 'twitter_scrape_status' | 'telegram_scrape_status'
}

const scrapeTypes: ScrapeType[] = [
  { key: 'email', label: '邮箱信息', field: 'email', statusField: 'email_scrape_status' },
  { key: 'twitter', label: '推特信息', field: 'twitter_handle', statusField: 'twitter_scrape_status' },
  { key: 'telegram', label: 'Telegram群组', field: 'telegram_group', statusField: 'telegram_scrape_status' },
]

function getOverallScrapeStatus(record: Project, scrapingNames: Set<string>): {
  status: 'not_scraped' | 'scraping' | 'scraped' | 'partial' | 'no_data'
  latestTime: string | null
} {
  const all = [
    record.email_scrape_status || 'NOT_SCRAPED',
    record.twitter_scrape_status || 'NOT_SCRAPED',
    record.telegram_scrape_status || 'NOT_SCRAPED',
  ] as ScrapeStatus[]

  const times = [
    record.email_scraped_at,
    record.twitter_scraped_at,
    record.telegram_scraped_at,
  ].filter(Boolean) as string[]

  const latestTime = times.length > 0
    ? times.reduce((a, b) => a > b ? a : b)
    : null

  if (all.every(s => s === 'NOT_SCRAPED')) {
    return { status: 'not_scraped', latestTime }
  }

  if (scrapingNames.has(record.name || '')) {
    return { status: 'scraping', latestTime }
  }

  const successCount = all.filter(s => s === 'SCRAPED_SUCCESS').length

  if (successCount === 3) {
    return { status: 'scraped', latestTime }
  }

  if (successCount > 0) {
    return { status: 'partial', latestTime }
  }

  return { status: 'no_data', latestTime }
}

function formatScrapeTime(isoStr: string | null): string {
  if (!isoStr) return ''
  try {
    const d = new Date(isoStr)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return '刚刚'
    if (diffMin < 60) return `${diffMin}分钟前`
    const diffHour = Math.floor(diffMin / 60)
    if (diffHour < 24) return `${diffHour}小时前`
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
  } catch {
    return ''
  }
}

const overallStatusConfig: Record<string, { color: string; text: string }> = {
  not_scraped: { color: 'default', text: '待抓取' },
  scraping: { color: 'processing', text: '抓取中' },
  scraped: { color: 'success', text: '已抓取' },
  partial: { color: 'warning', text: '部分抓取' },
  no_data: { color: 'error', text: '无数据' },
}

function ScrapeCenter() {
  const navigate = useNavigate()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedScrapeTypes, setSelectedScrapeTypes] = useState<string[]>(['email', 'twitter', 'telegram'])
  const [scraping, setScraping] = useState(false)
  const [progress, setProgress] = useState(0)
  const [forceRescrape, setForceRescrape] = useState(false)
  const [projectInfoDrawerOpen, setProjectInfoDrawerOpen] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [scrapeTaskId, setScrapeTaskId] = useState<string | null>(null)
  const [currentScrapeProject, setCurrentScrapeProject] = useState<string | null>(null)
  const [scrapeTotal, setScrapeTotal] = useState(0)
  const [scrapeCompleted, setScrapeCompleted] = useState(0)
  const [scrapingProjectNames, setScrapingProjectNames] = useState<Set<string>>(new Set())

  // 数据查询 — 支持搜索和分页，与项目管理页一致
  const { data, isLoading, refetch } = useProjects({
    page: currentPage,
    page_size: 200,
    search: searchText || undefined,
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  const projects = data?.items || []
  const totalProjects = data?.total || 0

  // 无网站的项目数量（用于提示）
  const noWebsiteCount = projects.filter(p => !p.website).length

  // 表格列定义
  const columns: TableProps<Project>['columns'] = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (text: string, record: Project) => text || record.website,
    },
    {
      title: '网站',
      dataIndex: 'website',
      key: 'website',
      width: 250,
      ellipsis: true,
      render: (text: string) => text ? (
        <a href={text} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{text}</a>
      ) : (
        <Tag color="red" icon={<WarningOutlined />}>无网站</Tag>
      ),
    },
    {
      title: '抓取状态',
      key: 'overall_scrape_status',
      width: 140,
      align: 'center',
      render: (_: any, record: Project) => {
        const { status, latestTime } = getOverallScrapeStatus(record, scrapingProjectNames)
        const cfg = overallStatusConfig[status]
        return (
          <Tooltip title={latestTime ? `最近抓取: ${formatScrapeTime(latestTime)}` : undefined}>
            <Tag color={cfg.color}>{cfg.text}</Tag>
            {latestTime && status !== 'scraping' && (
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{formatScrapeTime(latestTime)}</div>
            )}
          </Tooltip>
        )
      },
    },
    {
      title: '邮箱',
      key: 'email_status',
      width: 120,
      align: 'center',
      render: (_: any, record: Project) => {
        const status = (record.email_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
        const config = scrapeStatusConfig[status]
        return (
          <Tooltip title={record.email || '未抓取'}>
            <Tag color={config.color} icon={config.icon}>
              {config.text}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: '推特',
      key: 'twitter_status',
      width: 120,
      align: 'center',
      render: (_: any, record: Project) => {
        const status = (record.twitter_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
        const config = scrapeStatusConfig[status]
        return (
          <Tooltip title={record.twitter_handle || '未抓取'}>
            <Tag color={config.color} icon={config.icon}>
              {config.text}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: 'Telegram',
      key: 'telegram_status',
      width: 200,
      render: (_: any, record: Project) => {
        const status = (record.telegram_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
        const config = scrapeStatusConfig[status]
        const admins = (record as any).telegram_admins || []
        return (
          <div>
            <Tooltip title={record.telegram_group || '未抓取'}>
              <Tag color={config.color} icon={config.icon}>
                {config.text}
              </Tag>
            </Tooltip>
            {record.telegram_group && (
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                <a href={record.telegram_group} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                  {record.telegram_group.replace('https://t.me/', 't.me/')}
                </a>
              </div>
            )}
            {admins.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {admins.slice(0, 3).map((a: any, i: number) => (
                  <Tag key={i} color="purple" style={{ fontSize: 11, marginBottom: 2 }}>
                    @{a.username || '匿名'}
                  </Tag>
                ))}
                {admins.length > 3 && (
                  <Tag style={{ fontSize: 11 }}>+{admins.length - 3}</Tag>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: '触达状态',
      key: 'outreach_status',
      width: 120,
      align: 'center',
      render: (_: any, record: Project) => {
        const count = record.outreach_count || 0
        if (count === 0) {
          return <Tag color="default">未触达</Tag>
        }
        return (
          <Tooltip title={`已触达 ${count} 次`}>
            <Tag color="success">已触达 {count}次</Tag>
          </Tooltip>
        )
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_: any, record: Project) => {
        // 检查是否有已抓取的信息
        const hasEmail = record.email_scrape_status === 'SCRAPED_SUCCESS'
        const hasTwitter = record.twitter_scrape_status === 'SCRAPED_SUCCESS'
        const hasTelegram = record.telegram_scrape_status === 'SCRAPED_SUCCESS'
        const canOutreach = hasEmail || hasTwitter || hasTelegram

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedProjectId(record.id)
                setProjectInfoDrawerOpen(true)
              }}
            >
              查看详情
            </Button>
            <Tooltip title={canOutreach ? '创建触达任务' : '请先抓取至少一种信息'}>
              <Button
                type="link"
                size="small"
                icon={<SendOutlined />}
                disabled={!canOutreach}
                onClick={() => navigate(`/outreach?project_id=${record.id}`)}
              >
                触达
              </Button>
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  // 批量抓取处理
  const handleBatchScrape = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个项目')
      return
    }

    if (selectedScrapeTypes.length === 0) {
      message.warning('请至少选择一种抓取类型')
      return
    }

    // 过滤掉既没有网站也没有 Twitter handle 的项目
    const validIds = selectedRowKeys.filter(key => {
      const proj = projects.find(p => p.id === key)
      return proj?.website || proj?.twitter_handle
    })
    const skippedCount = selectedRowKeys.length - validIds.length

    if (validIds.length === 0) {
      message.warning('所选项目都没有网站 URL 或 Twitter Handle，无法抓取。请先在项目详情中填写网站地址或 Twitter。')
      return
    }

    if (skippedCount > 0) {
      message.info(`已跳过 ${skippedCount} 个无网站项目，将抓取 ${validIds.length} 个项目`)
    }

    setScraping(true)
    setProgress(0)
    setScrapeCompleted(0)
    setScrapeTotal(validIds.length)
    setCurrentScrapeProject(null)

    // 标记参与抓取的项目为"抓取中"
    const scrapingNames = new Set(
      validIds.map(id => projects.find(p => p.id === id)?.name || '').filter(Boolean)
    )
    setScrapingProjectNames(scrapingNames)

    try {
      // 提交任务（立即返回）
      const submitResult = await scrapeApi.batchScrape({
        project_ids: validIds.map(String),
        scrape_types: selectedScrapeTypes,
        force: forceRescrape,
      })

      const taskId = submitResult.task_id
      if (!taskId) {
        // 旧版兼容：没有 task_id 说明同步完成
        setProgress(100)
        message.success(`抓取完成！成功: ${submitResult.success || 0}/${submitResult.total || 0}`)
        refetch()
        setSelectedRowKeys([])
        setScraping(false)
        return
      }

      setScrapeTaskId(taskId)
      message.info('抓取任务已提交，后台处理中...')

      // 轮询进度
      const pollInterval = setInterval(async () => {
        try {
          const status = await scrapeApi.getTaskStatus(taskId)
          setScrapeCompleted(status.completed)
          setProgress(status.progress_percent)
          setCurrentScrapeProject(status.current_project)

          if (status.status === 'completed') {
            clearInterval(pollInterval)
            setScraping(false)
            setScrapeTaskId(null)
            setCurrentScrapeProject(null)
            setScrapingProjectNames(new Set())
            refetch()
            setSelectedRowKeys([])

            Modal.info({
              title: '抓取完成',
              width: 600,
              content: (
                <div>
                  <p>总数: {status.total} | 成功: {status.success} | 失败: {status.failed}</p>
                  {status.results && status.results.length > 0 && (
                    <div style={{ marginTop: 12, maxHeight: 300, overflow: 'auto' }}>
                      {status.results.map((r: any, idx: number) => (
                        <div key={idx} style={{ marginBottom: 6, padding: 6, background: '#f5f5f5', fontSize: 12 }}>
                          <strong>{r.project_name}</strong>
                          {r.email && <span> | 邮箱: {r.email.status === 'success' ? '✓' : '✗'}</span>}
                          {r.twitter && <span> | 推特: {r.twitter.status === 'success' ? '✓' : '✗'}</span>}
                          {r.telegram && <span> | TG: {r.telegram.status === 'success' ? '✓' : r.telegram.status === 'partial' ? '⚠' : '✗'}</span>}
                          {r.error && <span style={{ color: 'red' }}> | {r.error}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ),
            })
          }
        } catch {
          // 轮询失败，静默
        }
      }, 2000)

    } catch (error: any) {
      message.error(`抓取失败: ${error.message || '未知错误'}`)
      setScraping(false)
      setScrapingProjectNames(new Set())
      setProgress(0)
    }
  }

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: Project) => ({
      disabled: scrapingProjectNames.has(record.name || ''),
      name: record.name,
    }),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: 'select_not_scraped',
        text: '选择未抓取项目',
        onSelect: () => {
          const notScrapedKeys = projects
            .filter(p =>
              selectedScrapeTypes.some(type => {
                const scrapeType = scrapeTypes.find(st => st.key === type)
                if (!scrapeType) return false
                return (p[scrapeType.statusField] as ScrapeStatus) === 'NOT_SCRAPED'
              })
            )
            .map(p => p.id)
          setSelectedRowKeys(notScrapedKeys)
        },
      },
    ],
  }

  return (
    <div>
      <Card
        title={`信息抓取 (${totalProjects} 个项目)`}
        extra={
          <Space>
            <Input.Search
              placeholder="搜索项目名称 / 网站 / Twitter"
              allowClear
              onSearch={(value) => { setSearchText(value); setCurrentPage(1); setSelectedRowKeys([]); }}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              刷新
            </Button>
          </Space>
        }
      >
        {/* 无网站项目提示 */}
        {noWebsiteCount > 0 && (
          <Alert
            message={`${noWebsiteCount} 个项目没有网站 URL，无法抓取`}
            description="这些项目在抓取时会被自动跳过。如需抓取，请先在项目详情中填写网站地址。"
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 抓取类型选择 */}
        <Card type="inner" title="选择抓取类型" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Checkbox.Group
              value={selectedScrapeTypes}
              onChange={(values) => setSelectedScrapeTypes(values as string[])}
            >
              <Space direction="vertical">
                {scrapeTypes.map(type => (
                  <Checkbox key={type.key} value={type.key}>
                    {type.label}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
            <Checkbox
              checked={forceRescrape}
              onChange={(e) => setForceRescrape(e.target.checked)}
            >
              强制重新抓取（包括已抓取的项目）
            </Checkbox>
          </Space>
        </Card>

        {/* 操作按钮 */}
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            loading={scraping}
            disabled={selectedRowKeys.length === 0 || selectedScrapeTypes.length === 0}
            onClick={handleBatchScrape}
          >
            开始抓取 ({selectedRowKeys.length} 个项目)
          </Button>
          <Button onClick={() => setSelectedRowKeys([])}>
            清除选择
          </Button>
          <span style={{ marginLeft: 8 }}>
            已选择 {selectedRowKeys.length} / {totalProjects} 个项目
          </span>
        </Space>

        {/* 进度条 */}
        {scraping && (
          <div style={{ marginBottom: 16 }}>
            <Progress
              percent={progress}
              status="active"
              format={() => `${scrapeCompleted}/${scrapeTotal}`}
            />
            {currentScrapeProject && (
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                正在抓取: {currentScrapeProject}
              </div>
            )}
          </div>
        )}

        {/* 项目列表表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={projects}
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize: 50,
            total: totalProjects,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个项目`,
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 项目详情抽屉 */}
      <ProjectInfoDrawer
        projectId={selectedProjectId}
        visible={projectInfoDrawerOpen}
        onClose={() => {
          setProjectInfoDrawerOpen(false)
          setSelectedProjectId(null)
        }}
      />
    </div>
  )
}

export default ScrapeCenter
